import Empty from "antd/lib/empty";
import AntList from "antd/lib/list";
import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ListItemBase, ListProps } from "./List.types";
import { useSelection } from "./useSelection";

let listIdCounter = 0;

/**
 * Grid component implementing WAI-ARIA Grid pattern
 *
 * This component treats both simple lists and multi-column grids as grids:
 * - Simple list = grid with 1 column
 * - Multi-column layout = grid with N columns
 *
 * Implements full WAI-ARIA Grid keyboard navigation:
 * - ArrowUp/Down/Left/Right: Navigate between cells (stops at boundaries)
 * - Home/End: First/last cell in current row
 * - Ctrl+Home/End: First/last cell in entire grid
 * - PageUp/PageDown: Jump 10 rows
 * - Enter/Space: Activate/select cell
 * - F2: Enter cell interaction mode
 * - Escape: Exit cell interaction mode
 * - Tab: Exit grid to next focusable element
 *
 * Structure: grid > row > gridcell
 *
 * @param items - The items to display
 * @param selectionMode - The selection mode (single or multiple)
 * @param selectedKeys - The selected keys
 * @param showSelectionIndicator - Whether to show selection indicator (checkbox/radio)
 * @param emptyState - The empty state to display when items are empty
 * @param loading - The loading state
 * @param className - The class name to apply to the grid
 * @param rootClassName - The container class name
 * @param grid - Multi-column grid configuration (default: 1 column = simple list)
 * @param aria-label - The accessible name that identifies the collection
 * @param onSelectionChange - Callback when selection changes
 * @param renderItem - Function to render items
 */
export const List = <T extends ListItemBase>({
    items,
    selectionMode,
    selectedKeys,
    showSelectionIndicator = true,
    "aria-label": ariaLabel,
    emptyState,
    loading = false,
    className,
    rootClassName,
    grid,
    onSelectionChange,
    renderItem,
}: ListProps<T>) => {
    if (items.length > 0 && !renderItem) {
        throw new Error("renderItem is required when items are provided");
    }

    if (selectionMode && (!selectedKeys || !onSelectionChange)) {
        throw new Error("selectedKeys and onSelectionChange are required when selectionMode is provided");
    }
    const [t] = useTranslation(undefined, { keyPrefix: "@sol.uikit.dataDisplay.List" });
    const [listId] = useState(() => `list-${++listIdCounter}`);

    const hasSelection = Boolean(selectionMode);

    const handleActivate = useCallback(
        (key: string) => {
            if (!hasSelection || !selectedKeys || !onSelectionChange) {
                return;
            }

            if (selectionMode === "single") {
                onSelectionChange([key]);
            } else {
                const selectedSet = new Set(selectedKeys);
                if (selectedSet.has(key)) {
                    selectedSet.delete(key);
                } else {
                    selectedSet.add(key);
                }
                onSelectionChange([...selectedSet]);
            }
        },
        [hasSelection, selectedKeys, onSelectionChange, selectionMode],
    );

    const { getKeyByIndex, isDisabledAt } = useMemo(
        () => ({
            getKeyByIndex: (index: number) => items[index]?.key ?? `item-${index}`,
            isDisabledAt: (index: number) => Boolean(items[index]?.disabled),
        }),
        [items],
    );

    const columns = grid?.column || 1;

    const { focusedIndex, getItemProps, activeDescendantId, cellMode, interactiveCellIndex } = useSelection({
        itemCount: items.length,
        getKeyByIndex,
        onActivate: handleActivate,
        isDisabledAt,
        hasSelection,
        listId,
        columns,
    });

    const enhancedRenderItem = useCallback(
        (item: T, index: number) => {
            const isSelected = hasSelection ? selectedKeys?.includes(item.key) : undefined;
            const isDisabled = Boolean(item.disabled);
            const isFocused = index === focusedIndex;

            const handleItemClick = () => {
                if (!isDisabled && hasSelection) {
                    handleActivate(item.key);
                }
            };

            const itemProps = getItemProps(index);

            if (!renderItem) {
                return null;
            }

            const isInInteractionMode = cellMode === "interaction" && interactiveCellIndex === index;
            const renderedItem = renderItem({
                item,
                index,
                props: {
                    selected: isSelected,
                    selectionMode: hasSelection ? selectionMode : undefined,
                    showSelectionIndicator: hasSelection ? showSelectionIndicator : undefined,
                    onClick: hasSelection ? handleItemClick : undefined,
                    disabled: isDisabled,
                    focused: isFocused,
                    isInInteractionMode,
                    ...itemProps,
                },
            });

            return grid ? (
                <div key={item.key} className="flex h-full">
                    {renderedItem}
                </div>
            ) : (
                renderedItem
            );
        },
        [
            hasSelection,
            selectedKeys,
            selectionMode,
            showSelectionIndicator,
            focusedIndex,
            handleActivate,
            getItemProps,
            renderItem,
            cellMode,
            interactiveCellIndex,
            grid,
        ],
    );

    const containerClasses = useMemo(() => clsx("outline-hidden", rootClassName), [rootClassName]);

    const ariaProps = useMemo(
        () => ({
            role: "grid",
            "aria-label": ariaLabel ?? (hasSelection ? `Grille avec sélection ${selectionMode}` : "Grille"),
            "aria-activedescendant": hasSelection ? activeDescendantId : undefined,
            "aria-readonly": !hasSelection ? true : undefined,
        }),
        [activeDescendantId, ariaLabel, hasSelection, selectionMode],
    );

    if (items.length === 0 && !loading) {
        return (
            <div className={containerClasses} {...ariaProps}>
                {emptyState ?? <Empty description={t("empty")} />}
            </div>
        );
    }

    return (
        <div className={containerClasses} {...ariaProps}>
            <AntList
                className={clsx(
                    "[&_.ant-list-items]:flex [&_.ant-list-items]:flex-col [&_.ant-list-items]:gap-ant-xs",
                    className,
                )}
                loading={{ spinning: loading, tip: t("loading") }}
                dataSource={items}
                renderItem={enhancedRenderItem}
                split={false}
                grid={grid}
            />
        </div>
    );
};
